import styles from './flag.module.css';
import reactStringReplace from 'react-string-replace';
import classnames from 'classnames';
import React from 'react';
import { EVENT_NUMBER_MENTION_TRIGGER } from '../lexical/mention-plugin/constants';
import { DataPrivacy } from '../datadog-privacy';

/**
 *  for displaying
 *   !caution!
 *   ^vehicle^
 *   #0830230001
 *
 * NOTE: caution and vehicle are flag-node, created by flag-plugin.
 *       event number are mention-node, created by mention-plugin (require interaction to query from BE when create a new one),
 *       however when display event number in messaging, it's a static string, we only need some extra css to render the style,
 *       so use FlagRenderer to render event number.
 */

const CAUTION_REGEX = /!([^\s^!]*)\b!/;
// eslint-disable-next-line no-useless-escape
const VEHICLE_REGEX = /\^([^\s^(\^)]*)\b\^/;
const VALID_VEHICLE_REGEX = /(^((A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|PA|RI|S[CD]|T[NX]|UT|V[AT]|W[AIVY])-)?[\w]{1,17}$)/i;
const EVENT_NUMBER_REGEX = /(#[\w]{3,})/;

export function FlagRenderer({
    text,
    agencyEventNumberToIdMap,
    isFirstResponder,
    onAgencyEventNumberClick,
}: {
    text?: string | null;
    agencyEventNumberToIdMap?: { [key: string]: number };
    isFirstResponder?: boolean;
    onAgencyEventNumberClick?: ({
        agencyEventNumber,
        eventId,
    }: {
        agencyEventNumber: string;
        eventId: number;
    }) => void;
}): React.ReactElement {
    const safeText = text ?? '';
    let textWithFlags = reactStringReplace(safeText, CAUTION_REGEX, (match, i) => (
        <span
            className={classnames(styles['flag'], styles['caution'], {
                [styles['flag-lg']]: isFirstResponder,
            })}
            data-testid="FLAG"
            key={`caution-${i}`}
        >
            {match}
        </span>
    ));

    // ensure vehicle key is unique; reactStringReplace doesn't guarantee unique index for array input text
    let i = 0;
    textWithFlags = reactStringReplace(textWithFlags, VEHICLE_REGEX, (match) =>
        VALID_VEHICLE_REGEX.exec(match) ? (
            <span
                className={classnames(styles['flag'], styles['vehicle'], {
                    [styles['flag-lg']]: isFirstResponder,
                })}
                data-testid="FLAG"
                key={`vehicle-${i++}`}
            >
                {match}
            </span>
        ) : (
            match
        )
    );

    if (agencyEventNumberToIdMap && Object.keys(agencyEventNumberToIdMap).length > 0) {
        textWithFlags = reactStringReplace(
            textWithFlags,
            EVENT_NUMBER_REGEX,
            (possibleEventNumberWithPrefix, i) => {
                const agencyEventNumber = possibleEventNumberWithPrefix.replace(
                    EVENT_NUMBER_MENTION_TRIGGER,
                    ''
                );
                const eventId = agencyEventNumberToIdMap?.[agencyEventNumber];
                if (!eventId) {
                    // if eventId is not found in agencyEventNumberToIdMap, just return possibleEventNumberWithPrefix as raw text
                    return possibleEventNumberWithPrefix;
                }
                return (
                    <span
                        key={`event-number-${i}`}
                        className={classnames(styles['flag'], styles['event-number'], {
                            [styles['flag-lg']]: isFirstResponder,
                        })}
                        data-testid="FLAG"
                        data-eventid={eventId}
                        // This span has no tabIndex, so without preventDefault
                        // the browser's default mousedown action walks up to
                        // the nearest focusable ancestor (the row) and focuses
                        // THAT instead — which the row reads as "I've been
                        // selected". Blocking the focus-shift here stops that
                        // side effect at its source; stopPropagation on click
                        // below additionally guards the row's own
                        // click-to-select/deselect toggle from seeing this
                        // click at all. Neither this span nor the row's own
                        // handlers are affected: the click itself still fires
                        // normally afterward.
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={(event) => {
                            event.stopPropagation();
                            onAgencyEventNumberClick?.({
                                agencyEventNumber,
                                eventId,
                            });
                        }}
                    >
                        <span>{possibleEventNumberWithPrefix}</span>
                    </span>
                );
            }
        );
    }

    return (
        <DataPrivacy privacy="mask">
            <span>{textWithFlags}</span>
        </DataPrivacy>
    );
}
